import React, { useId, useRef, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import { Info } from 'lucide-react';

interface ColumnInfoButtonProps {
    /** Explanation shown in the tooltip. */
    text: string;
    /** Accessible name for the icon button. */
    label?: string;
}

/**
 * Info icon for a board column title. A tap or click toggles the tooltip, so it
 * works on touch screens; a mouse hover also shows it on desktop. Escape or a
 * click/tap outside closes it. Uses react-bootstrap's positioned tooltip, which
 * stays inside the viewport and is out of layout while hidden.
 */
const ColumnInfoButton: React.FC<ColumnInfoButtonProps> = ({ text, label = 'Mer information' }) => {
    const target = useRef<HTMLButtonElement>(null);
    const tooltipId = `column-info-${useId().replace(/:/g, '')}`;
    // Hover only counts for real mouse pointers; touch taps go through onClick.
    const [hovered, setHovered] = useState(false);
    const [pinned, setPinned] = useState(false);
    const show = hovered || pinned;

    const hide = () => {
        setHovered(false);
        setPinned(false);
    };

    return (
        <>
            <button
                ref={target}
                type="button"
                className="column-info-button ms-1"
                aria-label={label}
                aria-describedby={show ? tooltipId : undefined}
                onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
                onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
                onClick={() => setPinned((isPinned) => !isPinned)}
            >
                <Info size={15} aria-hidden="true" />
            </button>
            <Overlay target={target} show={show} placement="bottom" rootClose onHide={hide}>
                {(props) => (
                    <Tooltip id={tooltipId} className="column-info-tooltip-popup" {...props}>
                        {text}
                    </Tooltip>
                )}
            </Overlay>
        </>
    );
};

export default ColumnInfoButton;
